import React, { useEffect, useRef } from "react";
import {
  PawIcon, ShieldCheckIcon, HeartIcon,
  SparklesIcon,
} from "./Icons";

/* ── Extra icons needed only in About ───────────────────────────────────── */
const TargetIcon = (p) => (
  <svg width={p.size||20} height={p.size||20} viewBox="0 0 24 24" fill="none"
    stroke={p.color||"currentColor"} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <circle cx="12" cy="12" r="6"/>
    <circle cx="12" cy="12" r="2"/>
  </svg>
);

const GlobeIcon = (p) => (
  <svg width={p.size||20} height={p.size||20} viewBox="0 0 24 24" fill="none"
    stroke={p.color||"currentColor"} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const LeafIcon = (p) => (
  <svg width={p.size||20} height={p.size||20} viewBox="0 0 24 24" fill="none"
    stroke={p.color||"currentColor"} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 8C8 10 5.9 16.17 3.82 19.11a1 1 0 0 0 .9 1.5h.6c.6 0 1.1-.35 1.4-.8C8.5 17 11 14 17 14V8z"/>
    <path d="M17 8V3h-5c0 3 1.5 5 5 5z"/>
  </svg>
);

const MessageCircleIcon = (p) => (
  <svg width={p.size||20} height={p.size||20} viewBox="0 0 24 24" fill="none"
    stroke={p.color||"currentColor"} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const ZapIcon = (p) => (
  <svg width={p.size||20} height={p.size||20} viewBox="0 0 24 24" fill="none"
    stroke={p.color||"currentColor"} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);

const About = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("animate-in");
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll(".fade-up");
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const values = [
    { Icon: PawIcon,           title: "Compassion",  desc: "We treat every pet with the love and care they deserve, just like family.",                                          color: "#7C3AED" },
    { Icon: SparklesIcon,      title: "Excellence",  desc: "We uphold the highest grooming standards with skilled hands and premium products.",                                  color: "#EC4899" },
    { Icon: ShieldCheckIcon,   title: "Trust",       desc: "Safety and transparency are at the core of every appointment we handle.",                                           color: "#F97316" },
    { Icon: LeafIcon,          title: "Wellness",    desc: "We promote holistic pet well-being — clean, healthy, and happy tails.",                                             color: "#10B981" },
    { Icon: MessageCircleIcon, title: "Community",   desc: "We build lasting bonds with pet owners through open communication and care.",                                        color: "#6366F1" },
    { Icon: ZapIcon,           title: "Innovation",  desc: "We continuously improve our services to bring the best experience to every pet.",                                    color: "#F59E0B" },
  ];

  return (
    <>
      <style>{`
        .fade-up {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .fade-up.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
        .fade-up:nth-child(1) { transition-delay: 0.05s; }
        .fade-up:nth-child(2) { transition-delay: 0.15s; }
        .fade-up:nth-child(3) { transition-delay: 0.25s; }
        .fade-up:nth-child(4) { transition-delay: 0.35s; }
        .fade-up:nth-child(5) { transition-delay: 0.45s; }
        .fade-up:nth-child(6) { transition-delay: 0.55s; }

        .mv-card {
          position: relative;
          overflow: hidden;
          transition: transform 0.35s ease, box-shadow 0.35s ease;
        }
        .mv-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 24px 48px rgba(168, 85, 247, 0.22);
        }
        .mv-card::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 100%);
          pointer-events: none;
        }

        .val-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .val-card:hover {
          transform: translateY(-5px) scale(1.02);
          box-shadow: 0 20px 40px rgba(168, 85, 247, 0.18);
        }

        .hero-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.35;
          pointer-events: none;
        }
        .divider-icon {
          display: flex;
          align-items: center;
          gap: 12px;
          justify-content: center;
          color: #a855f7;
        }
        .divider-icon::before,
        .divider-icon::after {
          content: "";
          flex: 1;
          max-width: 120px;
          height: 2px;
          background: linear-gradient(to right, transparent, #a855f7, transparent);
        }
      `}</style>

      <div className="relative overflow-hidden" ref={sectionRef}>

        {/* ── Hero Banner ── */}
        <section className="relative py-28 px-6 text-center overflow-hidden"
          style={{ background: "linear-gradient(135deg, #f5f3ff 0%, #fdf2f8 50%, #fff7ed 100%)" }}>
          <div className="hero-blob w-96 h-96 bg-purple-400 top-[-80px] left-[-80px]" />
          <div className="hero-blob w-72 h-72 bg-pink-400 bottom-[-60px] right-[-40px]" />

          <div className="relative z-10 max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-purple-700 bg-purple-100 mb-6 tracking-wide">
              <PawIcon size={15} color="#7C3AED" /> Our Story
            </span>
            <h1 className="text-5xl md:text-6xl font-extrabold mb-6"
              style={{ background: "linear-gradient(135deg, #7c3aed, #db2777)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              About FurpectTails
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
              A sanctuary where every tail wags with joy. We're more than a grooming salon —
              we're a family that loves your pets as our own.
            </p>
          </div>
        </section>

        {/* ── Mission & Vision Cards ── */}
        <section className="py-20 px-6" style={{ background: "#faf8ff" }}>
          <div className="max-w-5xl mx-auto">
            <div className="divider-icon mb-14 text-lg font-bold">
              <PawIcon size={18} color="#a855f7" /> Mission &amp; Vision
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

              {/* Mission */}
              <div className="mv-card rounded-3xl p-10 fade-up"
                style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)", boxShadow: "0 16px 40px rgba(124, 58, 237, 0.3)" }}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ background: "rgba(255,255,255,0.2)" }}>
                    <TargetIcon size={30} color="white" />
                  </div>
                  <h2 className="text-3xl font-extrabold text-white">Our Mission</h2>
                </div>
                <p className="text-purple-100 text-lg leading-relaxed">
                  To provide <span className="text-white font-semibold">exceptional, compassionate grooming services</span> that
                  enhance the health, happiness, and well-being of every pet we serve — while building genuine,
                  lasting relationships with the families who trust us with their beloved companions.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  {["Quality Care", "Pet Safety", "Owner Trust"].map((tag) => (
                    <span key={tag} className="px-4 py-2 rounded-full text-sm font-semibold text-purple-900"
                      style={{ background: "rgba(255,255,255,0.25)" }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Vision */}
              <div className="mv-card rounded-3xl p-10 fade-up"
                style={{ background: "linear-gradient(135deg, #db2777 0%, #ec4899 50%, #f472b6 100%)", boxShadow: "0 16px 40px rgba(219, 39, 119, 0.3)" }}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ background: "rgba(255,255,255,0.2)" }}>
                    <GlobeIcon size={30} color="white" />
                  </div>
                  <h2 className="text-3xl font-extrabold text-white">Our Vision</h2>
                </div>
                <p className="text-pink-100 text-lg leading-relaxed">
                  To be the most <span className="text-white font-semibold">trusted and innovative pet grooming platform</span> in
                  the community — setting the gold standard for pet care by combining heartfelt dedication
                  with modern technology, making every grooming experience seamless and stress-free.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  {["Community Leader", "Innovation", "Gold Standard"].map((tag) => (
                    <span key={tag} className="px-4 py-2 rounded-full text-sm font-semibold text-pink-900"
                      style={{ background: "rgba(255,255,255,0.25)" }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Core Values ── */}
        <section className="py-20 px-6"
          style={{ background: "linear-gradient(135deg, #fff7ed 0%, #fdf2f8 50%, #f5f3ff 100%)" }}>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16 fade-up">
              <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-pink-700 bg-pink-100 mb-4 tracking-wide">
                <HeartIcon size={14} color="#be185d" /> What We Stand For
              </span>
              <h2 className="text-4xl font-extrabold text-gray-800">Our Core Values</h2>
              <p className="text-gray-500 mt-4 text-lg max-w-xl mx-auto">
                Everything we do is guided by a set of principles that put pets and their people first.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((val, i) => (
                <div key={i} className="val-card bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-purple-100/60 fade-up">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                    style={{ background: `${val.color}15`, border: `1.5px solid ${val.color}30` }}>
                    <val.Icon size={26} color={val.color} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{val.title}</h3>
                  <p className="text-gray-500 leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Bottom CTA ── */}
        <section className="py-20 px-6 text-center"
          style={{ background: "linear-gradient(135deg, #7c3aed 0%, #db2777 100%)" }}>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-4xl font-extrabold text-white mb-5">
              Ready to Experience the FurpectTails Difference?
            </h2>
            <p className="text-purple-200 text-lg mb-10 flex items-center justify-center gap-2">
              Book a grooming session today and give your pet the love they deserve.
              <PawIcon size={18} color="rgba(216,180,254,0.8)" />
            </p>
          </div>
        </section>

      </div>
    </>
  );
};

export default About;
